
import { mplTokenMetadata, fetchAllDigitalAssetByOwner } from "@metaplex-foundation/mpl-token-metadata";
import { fetchAssetsByOwner } from "@metaplex-foundation/mpl-core";
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";

export const FetchUserTokens = async ({ address, queryClient, rpcEndpoint }) => {
    if (!address) throw new Error("Wallet address not provided.");
    if (!rpcEndpoint) throw new Error("RPC endpoint is not configured.");

    const cacheKey = `tokens_${address}`;
    const cacheExpiry = 86400000; // 24 hours

    const cached = localStorage.getItem(cacheKey);
    if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < cacheExpiry) {
            queryClient?.setQueryData(["tokens", address], data);
            return data;
        }
    }

    // Live incremental holder so we can update UI as we go.
    const interimTokensByMint = new Map();
    const pushToken = (token) => {
        if (!token || !token.mint) return;
        if (!interimTokensByMint.has(token.mint)) {
            interimTokensByMint.set(token.mint, token);
            const arr = Array.from(interimTokensByMint.values());
            queryClient?.setQueryData(["tokens", address], arr);
        }
    };

    //
    // 1) REGULAR + CORE NFTS
    //
    const umi = createUmi(rpcEndpoint).use(mplTokenMetadata());

    const digitalAssets = await fetchAllDigitalAssetByOwner(umi, address);
    const coreDigitalAssets = await fetchAssetsByOwner(umi, address, {
        skipDerivePlugins: false,
    });

    const mapDigitalAssetToToken = async (asset) => {
        const mintAddress = asset.mint.publicKey.toString();
        const metadataUri = asset.metadata?.uri || null;
        const collectionKey = asset.metadata?.collection?.value?.key || null;
        const amount = asset.amount;

        let metadata = null;
        let imageUrl = null;
        let externalUrl = null;

        if (metadataUri) {
            try {
                const response = await fetch(metadataUri);
                metadata = await response.json();
                imageUrl = metadata?.image || null;
                externalUrl = metadata?.external_url || null;
            } catch (e) {}
        }

        const files = metadata?.properties?.files || [];
        const categoryRaw = metadata?.properties?.category || "";

        const findByMimePrefix = (prefix) => {
            if (!Array.isArray(files) || !files.length) return null;
            return (
                files.find((f) => {
                    const mime = (f.type || f.mime || "").toLowerCase();
                    return mime.startsWith(prefix);
                }) || null
            );
        };

        const imageFile = findByMimePrefix("image/");
        const videoFile = findByMimePrefix("video/");
        const audioFile = findByMimePrefix("audio/");

        if (!imageUrl && imageFile?.uri) imageUrl = imageFile.uri;

        let mediaCategory = categoryRaw.toLowerCase();
        if (!mediaCategory) {
            if (videoFile) mediaCategory = "video";
            else if (audioFile) mediaCategory = "audio";
            else if (imageFile) mediaCategory = "image";
            else mediaCategory = "";
        }

        let animationUrl = null;
        let audioUrl = null;
        const rawAnimationUrl = metadata?.animation_url || null;

        if (mediaCategory === "video") {
            animationUrl = rawAnimationUrl || videoFile?.uri || null;
            if (audioFile?.uri) audioUrl = audioFile.uri;
        } else if (mediaCategory === "audio") {
            audioUrl = rawAnimationUrl || audioFile?.uri || null;
        } else {
            if (videoFile?.uri) animationUrl = videoFile.uri;
            if (!animationUrl && rawAnimationUrl) {
                const lower = rawAnimationUrl.toLowerCase();
                if (lower.includes("ext=mp4") || lower.endsWith(".mp4")) animationUrl = rawAnimationUrl;
                else if (lower.includes("ext=mp3") || lower.endsWith(".mp3")) audioUrl = rawAnimationUrl;
            }
            if (!audioUrl && audioFile?.uri) audioUrl = audioFile.uri;
        }

        const attributes = Array.isArray(metadata?.attributes) ? metadata.attributes : [];

        return {
            mint: mintAddress,
            name: metadata?.name || "Unknown",
            amount,
            type: "NFT",
            description: metadata?.description || "",
            symbol: metadata?.symbol || "Unknown",
            uri: metadataUri || "No Metadata URI",
            collectionKey: collectionKey || null,
            image: imageUrl || "No image URL",
            external_url: externalUrl || null,
            animationUrl,
            audioUrl,
            mediaType: mediaCategory,
            attributes,
        };
    };

    const mapCoreDigitalAssetToToken = async (asset) => {
        const mintAddress = asset.publicKey;
        const metadataUri = asset.uri || null;
        const collectionKey = asset.collection?.key || null;
        const amount = asset.amount;

        let metadata = null;
        let imageUrl = null;
        let externalUrl = null;

        if (metadataUri) {
            try {
                const response = await fetch(metadataUri);
                metadata = await response.json();
                imageUrl = metadata?.image || null;
                externalUrl = metadata?.external_url || null;
            } catch (e) {}
        }

        const files = metadata?.properties?.files || [];
        const categoryRaw = metadata?.properties?.category || "";

        const findByMimePrefix = (prefix) => {
            if (!Array.isArray(files) || !files.length) return null;
            return (
                files.find((f) => {
                    const mime = (f.type || f.mime || "").toLowerCase();
                    return mime.startsWith(prefix);
                }) || null
            );
        };

        const imageFile = findByMimePrefix("image/");
        const videoFile = findByMimePrefix("video/");
        const audioFile = findByMimePrefix("audio/");

        if (!imageUrl && imageFile?.uri) imageUrl = imageFile.uri;

        let mediaCategory = categoryRaw.toLowerCase();
        if (!mediaCategory) {
            if (videoFile) mediaCategory = "video";
            else if (audioFile) mediaCategory = "audio";
            else if (imageFile) mediaCategory = "image";
            else mediaCategory = "";
        }

        let animationUrl = null;
        let audioUrl = null;
        const rawAnimationUrl = metadata?.animation_url || null;

        if (mediaCategory === "video") {
            animationUrl = rawAnimationUrl || videoFile?.uri || null;
            if (audioFile?.uri) audioUrl = audioFile.uri;
        } else if (mediaCategory === "audio") {
            audioUrl = rawAnimationUrl || audioFile?.uri || null;
        } else {
            if (videoFile?.uri) animationUrl = videoFile.uri;
            if (!animationUrl && rawAnimationUrl) {
                const lower = rawAnimationUrl.toLowerCase();
                if (lower.includes("ext=mp4") || lower.endsWith(".mp4")) animationUrl = rawAnimationUrl;
                else if (lower.includes("ext=mp3") || lower.endsWith(".mp3")) audioUrl = rawAnimationUrl;
            }
            if (!audioUrl && audioFile?.uri) audioUrl = audioFile.uri;
        }

        const attributes = Array.isArray(metadata?.attributes) ? metadata.attributes : [];

        return {
            mint: mintAddress,
            name: asset.name || metadata?.name || "Unknown",
            amount,
            type: "Core NFT",
            description: metadata?.description || "",
            symbol: asset.data?.symbol || metadata?.symbol || "Unknown",
            uri: metadataUri || "No Metadata URI",
            collectionKey: asset.updateAuthority?.address || collectionKey || null,
            image: imageUrl || "No image URL",
            external_url: externalUrl || null,
            animationUrl,
            audioUrl,
            mediaType: mediaCategory,
            attributes,
        };
    };

    const digitalAssetsTokens = [];
    for (const asset of digitalAssets) {
        const token = await mapDigitalAssetToToken(asset);
        digitalAssetsTokens.push(token);
        pushToken(token);
    }

    const coreAssetsTokens = [];
    for (const asset of coreDigitalAssets) {
        const token = await mapCoreDigitalAssetToToken(asset);
        coreAssetsTokens.push(token);
        pushToken(token);
    }

    //
    // 2) COMPRESSED NFTS via DAS (same RPC endpoint)
    //
    const mapCompressedAssetToToken = async (asset) => {
        const assetId = asset.id;
        const jsonUri = asset.content?.json_uri || null;

        let metadata = asset.content?.metadata || null;
        let imageUrl = asset.content?.links?.image || null;
        let externalUrl = metadata?.external_url || asset.content?.links?.external || null;

        const needsRemoteMetadata =
            jsonUri &&
            (!metadata ||
                !metadata.image ||
                (!metadata.animation_url &&
                    !(metadata.properties && Array.isArray(metadata.properties.files) && metadata.properties.files.length)));

        if (needsRemoteMetadata) {
            try {
                const response = await fetch(jsonUri);
                const fetchedMetadata = await response.json();
                metadata = metadata ? { ...metadata, ...fetchedMetadata } : fetchedMetadata;
                if (!imageUrl) imageUrl = fetchedMetadata.image || null;
                if (!externalUrl) externalUrl = fetchedMetadata.external_url || null;
            } catch (e) {}
        }

        const filesFromMetadata = metadata?.properties?.files || [];
        const filesFromContent = asset.content?.files || [];
        const allFiles = [...filesFromMetadata, ...filesFromContent];

        let animationUrl = metadata?.animation_url || asset.content?.links?.animation_url || null;

        if (!animationUrl && allFiles.length) {
            const videoFile = allFiles.find((f) => ((f.type || f.mime || "").toLowerCase().startsWith("video/")));
            if (videoFile) animationUrl = videoFile.uri || null;
        }

        let audioUrl = null;
        if (allFiles.length) {
            const audioFile = allFiles.find((f) => ((f.type || f.mime || "").toLowerCase().startsWith("audio/")));
            if (audioFile) audioUrl = audioFile.uri || null;
        }

        if (!imageUrl && allFiles.length) {
            const imageFile =
                allFiles.find((f) => ((f.type || f.mime || "").toLowerCase().startsWith("image/"))) || allFiles[0];
            imageUrl = imageFile?.uri || null;
        }

        const collectionGrouping = asset.grouping?.find((g) => g.group_key === "collection") || null;

        const mediaCategory =
            (metadata?.properties?.category ||
                asset.content?.metadata?.properties?.category ||
                asset.content?.metadata?.category ||
                "").toLowerCase();

        const baseDescription = metadata?.description || asset.content?.metadata?.description || "";

        const attributes = Array.isArray(metadata?.attributes)
            ? metadata.attributes
            : Array.isArray(asset.content?.metadata?.attributes)
                ? asset.content.metadata.attributes
                : [];

        return {
            mint: assetId,
            assetId,
            name: metadata?.name || asset.content?.metadata?.name || "Unknown",
            amount: 1,
            type: "Compressed NFT",
            description: baseDescription,
            mediaType: mediaCategory,
            symbol: metadata?.symbol || asset.content?.metadata?.symbol || "Unknown",
            uri: jsonUri || "No Metadata URI",
            collectionKey: collectionGrouping?.group_value || null,
            image: imageUrl || "No image URL",
            external_url: externalUrl || null,
            animationUrl,
            audioUrl,
            attributes,
        };
    };

    const fetchCompressedTokensForOwner = async (ownerAddress) => {
        let compressedTokens = [];
        try {
            const dasBody = {
                jsonrpc: "2.0",
                id: "getAssetsByOwner",
                method: "getAssetsByOwner",
                params: { ownerAddress, page: 1, limit: 1000 },
            };

            const resp = await fetch(rpcEndpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dasBody),
            });

            const json = await resp.json();
            const cnftAssets = (json?.result?.items || []).filter((a) => a.compression?.compressed);

            for (const asset of cnftAssets) {
                const token = await mapCompressedAssetToToken(asset);
                compressedTokens.push(token);
                pushToken(token);
            }
        } catch (e) {
            console.error("Error fetching compressed NFTs via DAS:", e);
            compressedTokens = [];
        }
        return compressedTokens;
    };

    const compressedTokens = await fetchCompressedTokensForOwner(address);

    //
    // 3) MERGE + DEDUPE + SORT BY COLLECTION (same behavior)
    //
    const combinedTokens = [...digitalAssetsTokens, ...coreAssetsTokens, ...compressedTokens];

    const uniqueTokens = combinedTokens.reduce((acc, token) => {
        if (!acc.some((t) => t.mint === token.mint)) acc.push(token);
        return acc;
    }, []);

    uniqueTokens.sort((a, b) => {
        if (a.collectionKey && b.collectionKey) return a.collectionKey.localeCompare(b.collectionKey);
        if (a.collectionKey) return -1;
        if (b.collectionKey) return 1;
        return 0;
    });

    localStorage.setItem(cacheKey, JSON.stringify({ data: uniqueTokens, timestamp: Date.now() }));
    return uniqueTokens;
};
